import React, { useState, useRef, useEffect, Fragment } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import PinInput from 'react-pin-input';

import Wrapper from '../Login/WrapperOLD';

import { Button } from '../../components/Button';

import useAsync from '../../hooks/useAsync';
import useSearchParams from '../../hooks/useSearchParams';
import useLocal from '../../hooks/useLocal';

import { getPublicCompany } from '../../api/company';
import { addTicket, addTicketPublic, getPublicAsset } from '../../api';

import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import Badge from '../../components/Badge';
import moment from 'moment';
import classNames from 'classnames';
import Modal from '../../components/Shell/Modal';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
	TemplateField,
	TemplateImage,
	TemplateTextarea,
} from '../../components/Attributes/Attributes_old';
import Banner from '../../components/Banner';
import useSettings from '../../hooks/useSettings';

const HEALTH_STATUS_BUTTON = {
	operational: {
		label: 'Operational',
		value: 'success',
	},
	maintenance: {
		label: 'Maintenance',
		value: 'info',
	},
	caution: {
		label: 'Caution',
		value: 'caution',
	},
	down: { label: 'Down', value: 'error' },
	offline: {
		label: 'Offline',
		value: 'default',
	},
};

const Company = () => {
	const prevLocation = useLocation();
	const pinInputRef = useRef();

	const [isTicketModalOpen, setTicketModalOpen] = useState(false);
	const [form, setForm] = useState({});
	const [customPin, setCustomPin] = useState('');

	const navigate = useNavigate();
	const { id } = useParams();
	const { tag } = useSearchParams();

	const { get } = useLocal();

	const localUser = get('user');

	const [error, setError] = useState(null);
	const [fileList, setFileList] = useState([]);

	const {
		execute: onAdd,
		status,
		error: addTicketError,
	} = useAsync(addTicketPublic, {
		immediate: false,
	});

	const {
		value: company,
		status: gettingCompany,
		error: getCompanyFailed,
	} = useAsync(getPublicCompany, { defaultPayload: { id } });

	const {
		value: item,
		execute: onGetItem,
		status: isSearchingTag,
	} = useAsync(getPublicAsset, {
		immediate: false,
		showNotification: { error: 'raw' },
	});

	useEffect(() => {
		if (tag) {
			onGetItem({ tag, organizationId: id });
		}
	}, [tag]);

	useEffect(() => {
		if (localUser && item) {
			navigate(`/assets/${item?.id}/overview`);
		}
	}, [localUser, item]);

	const handleClose = () => {
		setTicketModalOpen(false);
		setForm({});
	};

	return (
		<>
			<Wrapper
				title={company?.name}
				message={getCompanyFailed && { value: getCompanyFailed?.message }}
			>
				{gettingCompany === 'pending' ? (
					<LoadingSkeleton />
				) : (
					<>
						{getCompanyFailed ? (
							<div className="space-y-6 text-xs ">
								<p>
									Please reach out to your lab operations manager or Scipods
									directly for support
								</p>
								<div className="flex justify-end">
									<Button onClick={() => navigate(`/login`)}>
										Back to Login
									</Button>
								</div>
							</div>
						) : item ? (
							<div className="space-y-6">
								<div>
									<div className="px-4 sm:px-0">
										<h3 className="text-base font-semibold leading-4 text-gray-900">
											Asset Information
										</h3>
										<p className="mt-1 max-w-2xl text-xs leading-6 text-gray-500">
											Details about the selected asset
										</p>
									</div>
									<div className="mt-6 border-t border-gray-100">
										<dl className="divide-y divide-gray-100">
											<div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
												<dt className="text-xs font-medium  text-gray-900">
													Asset
												</dt>
												<dd className="mt-1 text-xs text-gray-700 sm:col-span-2 sm:mt-0">
													{`${
														item?.manufacturerName || item?.manufacturer?.name
													} ${item?.modelName || item?.model?.name}`}
												</dd>
											</div>
											<div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
												<dt className="text-xs font-medium leading-6 text-gray-900">
													Serial Number
												</dt>
												<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
													{item?.serialNumber}
												</dd>
											</div>
											{item?.health && (
												<div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
													<dt className="text-xs font-medium leading-6 text-gray-900">
														Status
													</dt>
													<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
														{
															<Badge
																isBeta
																status={
																	HEALTH_STATUS_BUTTON[
																		item?.health || 'operational'
																	]?.value
																}
																size="xxs"
															>
																{
																	HEALTH_STATUS_BUTTON[
																		item?.health || 'operational'
																	]?.label
																}
															</Badge>
														}
													</dd>
												</div>
											)}
											{item?.location && (
												<div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
													<dt className="text-xs font-medium leading-6 text-gray-900">
														Location
													</dt>
													<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
														{item?.location?.name}
													</dd>
												</div>
											)}
											{item?.category && (
												<div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
													<dt className="text-xs font-medium leading-6 text-gray-900">
														Category
													</dt>
													<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
														{item?.category?.name}
													</dd>
												</div>
											)}
											{item?.nextPM?.date && (
												<div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
													<dt className="text-xs font-medium leading-6 text-gray-900">
														Next PM
													</dt>
													<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
														{moment(item?.nextPM?.date).fromNow()}
														<div className="text-gray-400">
															{moment(item?.nextPM?.date).format(
																'dddd, MMM Do, YYYY',
															)}
														</div>
													</dd>
												</div>
											)}
											{item?.customAttributes?.length
												? item?.customAttributes?.map((attr) => (
														<div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
															<dt className="text-xs font-medium leading-6 text-gray-900">
																{attr?.attribute_field?.label}
															</dt>
															<dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
																{attr?.value}
															</dd>
														</div>
													))
												: null}
										</dl>
									</div>
								</div>

								<div
									className={classNames(
										'flex',
										item?.reportButton?.enabled
											? 'justify-between'
											: 'justify-end',
									)}
								>
									{item?.reportButton?.enabled ? (
										<Button onClick={() => setTicketModalOpen(true)}>
											Report issue
										</Button>
									) : null}
									<Button
										type="secondary"
										onClick={() =>
											navigate(`/login?redirectTo=/assets/${item?.id}`)
										}
									>
										Login for more
									</Button>
								</div>

								<Dialog
									open={isTicketModalOpen}
									onClose={() => handleClose()}
									className="relative z-10"
								>
									<Dialog.Backdrop
										transition
										className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
									/>

									<div className="fixed inset-0 z-10 w-screen overflow-y-auto">
										<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
											<Dialog.Panel
												transition
												className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
											>
												{addTicketError?.message && (
													<Banner status="error" className="mb-4" size="xs">
														{`${addTicketError?.message}:`}
														<div className="font-semibold">
															{addTicketError?.missing?.length
																? addTicketError?.missing
																		?.split(',')
																		.map((field) =>
																			field === 'summary' ? 'Overview' : field,
																		)
																		.join(', ')
																: null}
														</div>
													</Banner>
												)}
												<div className="font-semibold">Report an Issue</div>
												<div className="grid gap-y-4 my-4">
													{item?.reportButton?.reportedBy && (
														<TemplateField
															name="Reported by"
															placeholder="Name or email"
															onChange={(index, reportedBy) =>
																setForm({ ...form, reportedBy })
															}
															value={form?.reportedBy}
														/>
													)}
													<TemplateField
														name="Overview"
														required
														onChange={(index, overview) =>
															setForm({ ...form, overview })
														}
														value={form?.overview}
													/>
													{item?.reportButton?.details && (
														<TemplateTextarea
															name="Details"
															onChange={(index, description) =>
																setForm({ ...form, description })
															}
															value={form?.description}
														/>
													)}
													{item?.reportButton?.photos && (
														<TemplateImage
															name="Photos"
															maxImages={1}
															onChange={(index, fileList) => {
																setForm({ ...form, fileList });
															}}
															previewImage={false}
														/>
													)}
												</div>
												<div className="flex justify-between">
													<Button
														type="secondary"
														onClick={() => handleClose()}
													>
														Cancel
													</Button>
													<Button
														onClick={() =>
															onAdd(
																configurePayload(form, company?.id, item),
																() => handleClose(),
																{
																	showNotification: {
																		success:
																			'Issue has been reported.  Thank you',
																	},
																},
															)
														}
													>
														Report issue
													</Button>
												</div>
											</Dialog.Panel>
										</div>
									</div>
								</Dialog>
							</div>
						) : (
							<div className="space-y-6">
								{!company?.assetTagPinConfiguration ||
								company?.assetTagPinConfiguration?.value === 'default' ? (
									<>
										<p className="text-gray-700 text-sm">
											For security purposes, please enter the 4 characters
											located on the asset tag
										</p>

										<PinInput
											ref={pinInputRef}
											length={4}
											style={{ display: 'flex', justifyContent: 'center' }}
											onChange={(value, index) => {
												if (error) {
													setError(null);
												}
												setCustomPin(value);
											}}
											inputStyle={{
												borderColor: 'hsl(0, 0%, 80%)',
												marginRight: 10,
											}}
											inputFocusStyle={{ borderColor: '#52a9d2' }}
											onComplete={(value) => {
												navigate(`/c/${id}?tag=${value}`);
											}}
											type="string"
											focus
										/>
									</>
								) : (
									<div>
										<p className="text-gray-700 text-sm mb-3">
											{`Please enter the ${company?.assetTagPinConfiguration?.label} you are looking for`}
										</p>

										<TemplateField
											className="ml-3"
											onChange={(index, customPin) => setCustomPin(customPin)}
											value={customPin}
										/>
									</div>
								)}

								<div>
									<Button
										type="primary"
										className="flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
										disabled={isSearchingTag === 'pending'}
										onClick={() => navigate(`/c/${id}?tag=${customPin}`)}
									>
										{isSearchingTag === 'pending'
											? 'Finding asset...'
											: 'Find Asset'}
									</Button>
								</div>
							</div>
						)}
					</>
				)}
			</Wrapper>
		</>
	);
};

const configurePayload = (form, organizationId, item) => ({
	information: {
		summary: form?.overview,
		reportedBy: form?.reportedBy,
		description: form?.description,
		organizationId,
		submittedFrom: item?.reportButton?.submittedFromValue || 'QR Code',
		assetId: item?.id,
	},
	files: form?.fileList?.map(({ original }) => original) || [],
	bucket: 'tickets',
});

export default Company;
